import {
   ICadenceStep,
   ICampaignConfiguration,
   IContentPool,
   TCadenceAction,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import {
   emptyPool,
   renumber,
} from '@forwardflow/features/campaign-v2/utils/config'

export type ContentSection =
   | 'description'
   | 'outboundScripts'
   | 'inboundScripts'
   | 'strategies'
   | 'conversationContent'
   | 'personas'
   | 'humanScripts'
export type WorkspaceSection =
   | 'overview'
   | 'audience'
   | 'cadence'
   | 'content'
   | 'voice'
   | 'team'
   | 'numbers'
   | 'review'
   | 'add-type'
export const CONTENT_SECTIONS: {
   id: ContentSection
   label: string
   icon: 'pen' | 'phone' | 'chat' | 'spark' | 'users'
}[] = [
   { id: 'description', label: 'Description', icon: 'pen' },
   { id: 'outboundScripts', label: 'Outgoing calls', icon: 'phone' },
   { id: 'strategies', label: 'Outbound content groups', icon: 'chat' },
   { id: 'conversationContent', label: 'Reply instructions', icon: 'chat' },
   { id: 'inboundScripts', label: 'Incoming calls', icon: 'phone' },
   { id: 'humanScripts', label: 'Human guides', icon: 'users' },
]
export const poolName = (
   config: ICampaignConfiguration,
   pool: IContentPool,
   labels: Record<number, string>
) =>
   config.subtypes.find((s) => s.poolId === pool.id)?.name ||
   `${labels[pool.businessTypeId] || 'Lead'} general`

/** Editing the description never replaces the pool, references or existing rules. */
export const describePool = (
   config: ICampaignConfiguration,
   poolId: string,
   name: string,
   description: string
): ICampaignConfiguration => {
   const pool = config.contentPools.find((p) => p.id === poolId)
   if (!pool) return config
   const found = config.subtypes.find((s) => s.poolId === poolId)
   if (found)
      return {
         ...config,
         subtypes: config.subtypes.map((s) =>
            s.id === found.id ? { ...s, name, description } : s
         ),
      }
   const categoryPools = new Set(
      config.contentPools
         .filter((p) => p.businessTypeId === pool.businessTypeId)
         .map((p) => p.id)
   )
   const priority =
      Math.max(
         -1,
         ...config.subtypes
            .filter((s) => categoryPools.has(s.poolId))
            .map((s) => s.priority)
      ) + 1
   return {
      ...config,
      subtypes: [
         ...config.subtypes,
         {
            id: `type-${crypto.randomUUID()}`,
            name,
            description,
            poolId,
            priority,
            conditions: [],
         },
      ],
   }
}
export const addLeadType = (
   config: ICampaignConfiguration,
   categoryId: number,
   name: string,
   description: string,
   copyPoolId?: string
) => {
   const copy = config.contentPools.find(
      (p) => p.id === copyPoolId && p.businessTypeId === categoryId
   )
   const id = `pool-${crypto.randomUUID()}`
   const pool: IContentPool = {
      ...(copy ? structuredClone(copy) : emptyPool(categoryId)),
      id,
      isDefault: !config.contentPools.some(
         (p) => p.businessTypeId === categoryId
      ),
   }
   const next = {
      ...config,
      businessTypeIds: [...new Set([...config.businessTypeIds, categoryId])],
      contentPools: [...config.contentPools, pool],
   }
   return { config: describePool(next, id, name, description), poolId: id }
}

export interface CadenceRecipe {
   days: number
   call: number
   sms: number
   email: number
   start: TCadenceAction
   time: string
   voicemail: boolean
}
export const PRESETS: { name: string; recipe: CadenceRecipe }[] = [
   {
      name: 'Balanced · 5 days',
      recipe: {
         days: 5,
         call: 2,
         sms: 2,
         email: 1,
         start: 'call',
         time: '09:00',
         voicemail: true,
      },
   },
   {
      name: 'Text first · 5 days',
      recipe: {
         days: 5,
         call: 2,
         sms: 3,
         email: 0,
         start: 'sms',
         time: '09:30',
         voicemail: false,
      },
   },
   {
      name: 'Gentle follow-up · 10 days',
      recipe: {
         days: 10,
         call: 2,
         sms: 3,
         email: 0,
         start: 'sms',
         time: '10:00',
         voicemail: false,
      },
   },
]
/** A deterministic preview. Never changes content pools or message-series lengths. */
export const buildCadence = (r: CadenceRecipe): ICadenceStep[] => {
   if (
      !Number.isInteger(r.days) ||
      r.days < 1 ||
      r.days > 365 ||
      !/^([01]\d|2[0-3]):[0-5]\d$/.test(r.time)
   )
      throw Error('Choose 1–365 days and a valid start time.')
   const counts = { call: r.call, sms: r.sms, email: r.email }
   if (
      Object.values(counts).some(
         (n) => !Number.isInteger(n) || n < 0 || n > 100
      )
   )
      throw Error('Choose 0–100 actions per channel.')
   const total = r.call + r.sms + r.email
   if (!total) throw Error('Add at least one call, text or email.')
   if (!counts[r.start])
      throw Error('Add at least one action for the starting channel.')
   const order: TCadenceAction[] = [
      r.start,
      ...(['call', 'sms', 'email'] as TCadenceAction[]).filter(
         (a) => a !== r.start
      ),
   ]
   const [h, m] = r.time.split(':').map(Number)
   const used = new Map<number, number>()
   return Array.from({ length: total }, (_, i) => {
      const action = order.find((a) => counts[a] > 0)!
      counts[action]--
      order.push(order.shift()!)
      const day =
         total === 1 ? 1 : 1 + Math.floor((i * (r.days - 1)) / (total - 1))
      const minutes = h * 60 + m + 30 * (used.get(day) || 0)
      if (minutes >= 1440)
         throw Error(
            'Too many steps on one day. Increase the duration or start earlier.'
         )
      used.set(day, (used.get(day) || 0) + 1)
      return {
         id: `step-${crypto.randomUUID()}`,
         order: i + 1,
         day,
         localTime: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
         action,
         ...(action === 'call'
            ? {
                 voicemail: r.voicemail
                    ? ('if-configured' as const)
                    : ('none' as const),
              }
            : {}),
      }
   })
}
export const editCadence = (
   steps: ICadenceStep[],
   id: string,
   patch: Partial<ICadenceStep>
) =>
   renumber(
      steps.map((s) => {
         if (s.id !== id) return s
         const next = { ...s, ...patch }
         if (next.action !== 'call') delete next.voicemail
         else next.voicemail ??= 'none'
         return next
      })
   )
/** Drag changes action order by moving actions between the existing time slots. IDs stay stable. */
export const moveCadence = (
   steps: ICadenceStep[],
   id: string,
   targetId: string
) => {
   const sorted = renumber(steps)
   const from = sorted.findIndex((s) => s.id === id),
      to = sorted.findIndex((s) => s.id === targetId)
   if (from < 0 || to < 0 || from === to) return steps
   const actions = [...sorted]
   actions.splice(to, 0, ...actions.splice(from, 1))
   return actions.map((a, i) => ({
      ...a,
      day: sorted[i].day,
      localTime: sorted[i].localTime,
      order: i + 1,
   }))
}
export const changesBetween = (
   before: ICampaignConfiguration,
   after: ICampaignConfiguration
): string[] => {
   const changes: string[] = []
   const changed = (a: unknown, b: unknown) =>
      JSON.stringify(a) !== JSON.stringify(b)
   if (changed(before.cadence, after.cadence))
      changes.push('Cadence timing or actions changed')
   if (changed(before.source, after.source))
      changes.push('Audience filters changed')
   if (
      changed(before.subtypes, after.subtypes) ||
      changed(before.businessTypeIds, after.businessTypeIds)
   )
      changes.push('Lead types or descriptions changed')
   if (changed(before.contentPools, after.contentPools))
      changes.push('Content or personality selections changed')
   if (changed(before.routing, after.routing))
      changes.push('Agent routing changed')
   if (changed(before.phoneNumbers, after.phoneNumbers))
      changes.push('Campaign numbers changed')
   if (
      before.conversationQuietHours !== after.conversationQuietHours ||
      changed(before.stopRules, after.stopRules) ||
      changed(before.incomingCallPoolId, after.incomingCallPoolId) ||
      changed(before.schedulePolicy, after.schedulePolicy)
   )
      changes.push('Scheduling or contact rules changed')
   return changes
}
